import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { X, ChevronRight } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import axiosInstance from '../utils/axiosConfig';
import {
    getNotificationRoute,
    getNotificationConfig,
    playNotificationSound,
    resolveNotificationNavigation,
    CHAT_CONFIG,
    BUZZ_CONFIG
} from '../utils/notificationHelpers';

const NotificationContext = createContext(null);

// Almacén global para la función navigate
let _navigateFn = null;

const TOAST_DURATION = 8000; // 8 segundos

// Mostrar notificacion nativa del navegador (cuando el tab no esta enfocado)
function showBrowserNotification(title, body, onClick, icon = '/logo192.png') {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible') return;

    const notif = new Notification(title, {
        body: body || '',
        icon,
        tag: `centrosura-${Date.now()}`
    });

    if (onClick) {
        notif.onclick = () => {
            window.focus();
            onClick();
            notif.close();
        };
    }
}

function getInitials(name) {
    if (!name) return '?';

    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0]?.toUpperCase())
        .join('') || '?';
}

function ToastAvatar({ src, fallback, accentColor, icon: IconComponent }) {
    const [imageError, setImageError] = useState(false);
    const showImage = Boolean(src) && !imageError;

    return (
        <div className="relative shrink-0">
            <div
                className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl border border-white/15 text-sm font-semibold text-white shadow-lg"
                style={{
                    background: showImage
                        ? '#0f172a'
                        : `linear-gradient(135deg, ${accentColor || '#6366f1'} 0%, rgba(255,255,255,0.14) 100%)`
                }}
            >
                {showImage ? (
                    <img
                        src={src}
                        alt={fallback}
                        className="h-full w-full object-cover"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <span>{getInitials(fallback)}</span>
                )}
            </div>

            {IconComponent && (
                <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border border-[#002244] bg-white text-[#003366] shadow-md">
                    <IconComponent size={11} />
                </div>
            )}
        </div>
    );
}

/**
 * Toast corporativo con fondo oscuro, acento de color, progress bar y sonido.
 */
function NotificationToast({ t, titulo, mensaje, config, onNavigate, duration, avatarUrl, avatarFallback }) {
    const IconComponent = config.icon;
    const showAvatar = Boolean(avatarUrl || avatarFallback);

    return (
        <div
            className={`
                relative overflow-hidden max-w-sm w-full cursor-pointer
                rounded-2xl shadow-2xl
                ${t.visible ? 'animate-toastIn' : 'animate-toastOut'}
            `}
            style={{
                background: 'linear-gradient(135deg, #003366 0%, #002244 100%)',
                boxShadow: '0 20px 40px rgba(0,51,102,0.35)',
            }}
            onClick={() => {
                toast.dismiss(t.id);
                onNavigate?.();
            }}
        >
            {/* Acento lateral blanco */}
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)]" />

            {/* Contenido */}
            <div className="flex items-center gap-3 px-5 py-4 pl-6">
                {showAvatar ? (
                    <ToastAvatar
                        src={avatarUrl}
                        fallback={avatarFallback}
                        accentColor={config.accent}
                        icon={IconComponent}
                    />
                ) : (
                    <div className="p-2.5 rounded-xl shrink-0 animate-toastIconPop bg-white/12">
                        <IconComponent size={20} className="text-white" />
                    </div>
                )}

                {/* Texto */}
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                        {titulo}
                    </p>
                    {mensaje && (
                        <p className="text-xs text-white/60 truncate mt-0.5">
                            {mensaje}
                        </p>
                    )}
                </div>

                {/* Chevron */}
                <ChevronRight size={16} className="text-white/40 shrink-0" />

                {/* Cerrar */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        toast.dismiss(t.id);
                    }}
                    className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white/70 shrink-0 transition-colors"
                >
                    <X size={14} />
                </button>
            </div>

            {/* Progress bar blanca */}
            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/10">
                <div
                    className="h-full rounded-full animate-toastProgress bg-white/80"
                    style={{ animationDuration: `${duration}ms` }}
                />
            </div>
        </div>
    );
}

// Toast de notificación del sistema
function showToast(notification) {
    const fallbackRoute = getNotificationRoute(notification);
    const config = getNotificationConfig(notification.tipo);

    playNotificationSound();

    toast.custom(
        (t) => (
            <NotificationToast
                t={t}
                titulo={notification.titulo}
                mensaje={notification.mensaje}
                config={config}
                duration={TOAST_DURATION}
                onNavigate={async () => {
                    if (!_navigateFn) return;
                    if (notification.referencia_tipo === 'tarea' && notification.referencia_id) {
                        const path = await resolveNotificationNavigation(notification);
                        _navigateFn(path);
                    } else {
                        _navigateFn(fallbackRoute);
                    }
                }}
            />
        ),
        { duration: TOAST_DURATION, position: 'top-right' }
    );
}

// Toast de chat
function showChatToast(senderName, preview, senderFoto) {
    playNotificationSound();

    toast.custom(
        (t) => (
            <NotificationToast
                t={t}
                titulo={senderName}
                mensaje={preview}
                config={CHAT_CONFIG}
                duration={5000}
                avatarUrl={senderFoto}
                avatarFallback={senderName}
                onNavigate={() => {
                    if (_navigateFn) _navigateFn('/chat');
                }}
            />
        ),
        { duration: 5000, position: 'top-right' }
    );
}

// Toast de zumbido (efímero, no se persiste en notificaciones)
export function showBuzzToast(senderName) {
    toast.custom(
        (t) => (
            <NotificationToast
                t={t}
                titulo={`${senderName} te envió un zumbido`}
                mensaje="Haz clic para ir al chat"
                config={BUZZ_CONFIG}
                duration={4000}
                avatarFallback={senderName}
                onNavigate={() => {
                    if (_navigateFn) _navigateFn('/chat');
                }}
            />
        ),
        { duration: 4000, position: 'top-right' }
    );
}

export const NotificationProvider = ({ children }) => {
    const { user } = useAuth();
    const { socket, connected } = useSocket();
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [latestNotification, setLatestNotification] = useState(null);
    const permissionRequested = useRef(false);

    const setNavigate = useCallback((fn) => {
        _navigateFn = fn;
    }, []);

    // Pedir permiso para notificaciones del navegador
    // Solo pregunta si el permiso está en 'default' (nunca respondido)
    // y no se ha preguntado en los últimos 7 días
    useEffect(() => {
        if (permissionRequested.current) return;
        if (!user?.id) return;
        if (!('Notification' in window)) return;

        if (Notification.permission === 'default') {
            const lastAsked = localStorage.getItem('notification_permission_asked_at');
            const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
            const shouldAsk = !lastAsked || (Date.now() - parseInt(lastAsked, 10)) > SEVEN_DAYS;

            if (shouldAsk) {
                Notification.requestPermission();
                localStorage.setItem('notification_permission_asked_at', Date.now().toString());
            }
        }
        permissionRequested.current = true;
    }, [user?.id]);

    const fetchUnreadCount = useCallback(async () => {
        if (!user?.id) return;

        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/notificaciones/count');
            setUnreadCount(response.data.count || 0);
        } catch (error) {
            console.error('Error fetching notification count:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchUnreadCount();
    }, [fetchUnreadCount]);

    // Polling fallback cada 5 minutos
    useEffect(() => {
        if (!user?.id) return;
        const interval = setInterval(fetchUnreadCount, 300000);
        return () => clearInterval(interval);
    }, [user?.id, fetchUnreadCount]);

    // Escuchar notificaciones push via Socket.IO
    useEffect(() => {
        if (!socket || !connected) return;

        const handleNewNotification = (notification) => {
            setUnreadCount(prev => prev + 1);
            setLatestNotification(notification);
            showToast(notification);

            const fallback = getNotificationRoute(notification);
            showBrowserNotification(
                notification.titulo,
                notification.mensaje,
                async () => {
                    if (!_navigateFn) return;
                    if (notification.referencia_tipo === 'tarea' && notification.referencia_id) {
                        const path = await resolveNotificationNavigation(notification);
                        _navigateFn(path);
                    } else {
                        _navigateFn(fallback);
                    }
                }
            );
        };

        const handleChatNotification = (data) => {
            const msg = data.message;
            // Los zumbidos tienen su propio toast — no duplicar
            if (msg?.content === '[BUZZ]') return;
            const senderName = msg?.sender_name || 'Nuevo mensaje';
            let preview;
            if (msg?.content?.startsWith('[STICKER]')) {
                preview = 'Sticker';
            } else if (msg?.content?.startsWith('[GIF]')) {
                preview = 'GIF';
            } else if (msg?.content?.startsWith('[CALL]')) {
                let info = null;
                try { info = JSON.parse(msg.content.substring('[CALL]'.length)); } catch { /* noop */ }
                if (info?.status === 'missed') preview = 'Llamada perdida';
                else if (info?.status === 'rejected') preview = 'Llamada rechazada';
                else if (info?.status === 'cancelled') preview = 'Llamada cancelada';
                else preview = info?.call_type === 'video' ? 'Videollamada' : 'Llamada';
            } else if (msg?.content) {
                preview = msg.content.length > 80 ? msg.content.substring(0, 80) + '...' : msg.content;
            } else if (msg?.file?.original_name) {
                preview = `Archivo: ${msg.file.original_name}`;
            } else {
                preview = 'Nuevo mensaje';
            }

            showChatToast(senderName, preview, msg?.sender_foto);
            showBrowserNotification(
                senderName,
                preview,
                () => { if (_navigateFn) _navigateFn('/chat'); },
                msg?.sender_foto || '/logo192.png'
            );
        };

        socket.on('new_notification', handleNewNotification);
        socket.on('message_notification', handleChatNotification);

        return () => {
            socket.off('new_notification', handleNewNotification);
            socket.off('message_notification', handleChatNotification);
        };
    }, [socket, connected]);

    const markAsRead = async (notificationId) => {
        if (!user?.id) return false;
        try {
            await axiosInstance.put(`/api/notificaciones/${notificationId}/read`);
            setUnreadCount(prev => Math.max(0, prev - 1));
            return true;
        } catch (error) {
            console.error('Error marking notification as read:', error);
            return false;
        }
    };

    const markAllAsRead = async () => {
        if (!user?.id) return false;
        try {
            await axiosInstance.put('/api/notificaciones/read-all');
            setUnreadCount(0);
            return true;
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            return false;
        }
    };

    const value = {
        unreadCount,
        loading,
        latestNotification,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        setNavigate
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications debe ser usado dentro de un NotificationProvider');
    }
    return context;
};
